import { useEffect, useMemo, useState } from "react";
import UserLayout from "../components/custom/layouts/UserLayout";
import { ToastTypes, useToast } from "../components/toast/ToastProvider";
import {
  AppSettings,
  editAppSettings,
  getSettings,
} from "../services/appSetting.service";
import "./AppSettings.css";

const normalizeGroup = (g: string) => g.trim().toLowerCase();
const normalizeKey = (k: string) => k.trim().toLowerCase();

const PRINTER_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: "thermal", label: "Thermal" },
  { value: "laser", label: "Laser" },
];

function formatSettingLabel(key: string): string {
  return key
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function groupIconClass(groupLabel: string): string {
  const g = normalizeGroup(groupLabel);
  if (g.includes("online")) return "fe-globe";
  if (g.includes("print")) return "fe-printer";
  if (g.includes("receipt") || g.includes("bill")) return "fe-file-text";
  if (g.includes("sales") || g.includes("purchase")) return "fe-layers";
  return "fe-sliders";
}

function groupSettingsByGroup(
  list: AppSettings[],
): { groupLabel: string; items: AppSettings[] }[] {
  const map = new Map<string, AppSettings[]>();
  const order: string[] = [];

  for (const s of list) {
    const key = normalizeGroup(s.setting_group);
    if (!map.has(key)) {
      map.set(key, []);
      order.push(key);
    }
    map.get(key)!.push(s);
  }

  const sortOnlineShopItems = (items: AppSettings[]): AppSettings[] => {
    const rank = (key: string) => {
      const nk = normalizeKey(key);
      if (nk === "shop_name") return 1;
      if (nk === "logo_url") return 2;
      if (nk === "icon_url") return 3;
      const slide = nk.match(/^slide_(\d)_url$/);
      if (slide) return 10 + Number(slide[1]);
      if (nk.startsWith("color_")) return 20;
      return 50;
    };
    return [...items].sort(
      (a, b) => rank(a.setting_key) - rank(b.setting_key),
    );
  };

  return order.map((key) => {
    const items = map.get(key)!;
    const sorted =
      key === "online shop" ? sortOnlineShopItems(items) : items;
    return { groupLabel: items[0].setting_group, items: sorted };
  });
}

const AppSetting = () => {
  const toast = useToast();

  const [settings, setSettings] = useState<AppSettings[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settingValues, setSettingValues] = useState<Record<number, string>>(
    {},
  );
  const [baselineValues, setBaselineValues] = useState<Record<number, string>>(
    {},
  );

  const valuesFromSettings = (list: AppSettings[]): Record<number, string> => {
    const next: Record<number, string> = {};
    list.forEach((s) => {
      if (s.id !== undefined) {
        next[s.id] = s.setting_value ?? "";
      }
    });
    return next;
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const settingList = await getSettings();
      setSettings(settingList);
      const loaded = valuesFromSettings(settingList);
      setSettingValues(loaded);
      setBaselineValues(loaded);
    } catch {
      toast("Failed to load settings", ToastTypes.ERROR);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const hasUnsavedChanges = useMemo(() => {
    return settings.some((s) => {
      if (s.id === undefined) return false;
      return (settingValues[s.id] ?? "") !== (baselineValues[s.id] ?? "");
    });
  }, [settings, settingValues, baselineValues]);

  const handleToggleSetting = (
    settingId: number | undefined,
    newValue: string,
  ) => {
    if (settingId === undefined) return;
    setSettingValues((prev) => ({
      ...prev,
      [settingId]: newValue,
    }));
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      const requests = settings.map((s) => {
        if (s.id === undefined) return Promise.resolve(null);
        return editAppSettings(
          {
            id: s.id,
            setting_group: s.setting_group,
            setting_key: s.setting_key,
            setting_value: String(settingValues[s.id] ?? ""),
          },
          s.id,
        );
      });
      await Promise.all(requests);
      setBaselineValues({ ...settingValues });
      toast("Settings updated successfully", ToastTypes.SUCCESS);
    } catch {
      toast("Failed to update settings", ToastTypes.ERROR);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettingValues({ ...baselineValues });
    toast("Unsaved changes reverted", ToastTypes.SUCCESS);
  };

  const renderControl = (setting: AppSettings) => {
    const id = setting.id;
    if (id === undefined) return null;

    const nk = normalizeKey(setting.setting_key);
    const raw = settingValues[id] ?? "";

    if (nk === "printer type") {
      const known = PRINTER_TYPE_OPTIONS.some((o) => o.value === raw);
      return (
        <select
          className="form-select app-settings-field app-settings-field--select"
          id={`setting-printer-${id}`}
          value={raw}
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        >
          {!known && raw ? <option value={raw}>{raw}</option> : null}
          {PRINTER_TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    }

    if (nk === "footer message") {
      return (
        <textarea
          className="form-control app-settings-field app-settings-field--textarea"
          id={`setting-footer-${id}`}
          rows={3}
          value={raw}
          placeholder="Footer text shown on printed receipts"
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        />
      );
    }

    const slideMatch = setting.setting_key.match(/^slide_(\d)_url$/i);
    if (slideMatch) {
      return (
        <div className="app-settings-input-stack">
          <div className="app-settings-input-wrap app-settings-input-wrap--url">
            <input
              type="url"
              className="form-control app-settings-field app-settings-field--url"
              id={`setting-slide-${id}`}
              value={raw}
              placeholder={`Hero slide ${slideMatch[1]} image URL`}
              onChange={(e) => handleToggleSetting(id, e.target.value)}
            />
          </div>
          {raw.trim() ? (
            <div className="app-settings-preview-box">
              <img
                src={raw.trim()}
                alt={`Slide ${slideMatch[1]} preview`}
                className="app-settings-slide-preview"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          ) : null}
        </div>
      );
    }

    if (nk === "icon_url") {
      return (
        <div className="app-settings-input-stack">
          <div className="app-settings-input-wrap app-settings-input-wrap--url">
            <input
              type="url"
              className="form-control app-settings-field app-settings-field--url"
              id={`setting-icon-${id}`}
              value={raw}
              placeholder="https://example.com/favicon.png"
              onChange={(e) => handleToggleSetting(id, e.target.value)}
            />
          </div>
          {raw.trim() ? (
            <div className="app-settings-preview-box">
              <img
                src={raw.trim()}
                alt="Tab icon preview"
                className="app-settings-slide-preview app-settings-logo-preview"
                style={{ width: 32, height: 32, objectFit: "contain" }}
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          ) : null}
        </div>
      );
    }

    if (nk === "logo_url") {
      return (
        <div className="app-settings-input-stack">
          <div className="app-settings-input-wrap app-settings-input-wrap--url">
            <input
              type="url"
              className="form-control app-settings-field app-settings-field--url"
              id={`setting-logo-${id}`}
              value={raw}
              placeholder="https://example.com/logo.png"
              onChange={(e) => handleToggleSetting(id, e.target.value)}
            />
          </div>
          {raw.trim() ? (
            <div className="app-settings-preview-box">
              <img
                src={raw.trim()}
                alt="Logo preview"
                className="app-settings-slide-preview app-settings-logo-preview"
                onError={(e) => {
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
          ) : null}
        </div>
      );
    }

    if (
      nk === "color_primary" ||
      nk === "color_secondary" ||
      nk === "color_accent"
    ) {
      const colorValue = /^#[0-9a-f]{6}$/i.test(raw) ? raw : "#ff6b00";
      return (
        <div className="app-settings-color-row">
          <input
            type="color"
            className="form-control form-control-color app-settings-color-picker"
            id={`setting-color-${id}`}
            value={colorValue}
            onChange={(e) => handleToggleSetting(id, e.target.value)}
            title={setting.setting_key}
          />
          <input
            type="text"
            className="form-control app-settings-field app-settings-field--hex"
            value={raw}
            placeholder="#FF6B00"
            maxLength={7}
            onChange={(e) => handleToggleSetting(id, e.target.value)}
          />
        </div>
      );
    }

    const isBool = raw === "true" || raw === "false";

    if (!isBool) {
      return (
        <input
          type="text"
          className="form-control app-settings-field"
          id={`setting-text-${id}`}
          value={raw}
          placeholder={`Enter ${formatSettingLabel(setting.setting_key).toLowerCase()}`}
          onChange={(e) => handleToggleSetting(id, e.target.value)}
        />
      );
    }

    const isOn = raw === "true";
    return (
      <div
        className={`app-settings-toggle ${isOn ? "is-on" : "is-off"}`}
      >
        <span className="app-settings-toggle-label">
          {isOn ? "SHOW" : "HIDE"}
        </span>
        <div className="form-check form-switch m-0">
          <input
            className="form-check-input"
            type="checkbox"
            role="switch"
            id={`setting-switch-${id}`}
            checked={isOn}
            onChange={(e) =>
              handleToggleSetting(id, e.target.checked ? "true" : "false")
            }
          />
        </div>
      </div>
    );
  };

  const grouped = groupSettingsByGroup(settings);
  const totalSettings = settings.length;

  const renderSettingRow = (setting: AppSettings) => {
    const controlId =
      setting.id !== undefined ? `setting-control-${setting.id}` : undefined;
    return (
      <div key={setting.id} className="app-settings-row">
        <div>
          <label
            className="app-settings-label"
            htmlFor={controlId}
          >
            {formatSettingLabel(setting.setting_key)}
          </label>
          {normalizeGroup(setting.setting_group).includes("online") &&
          normalizeKey(setting.setting_key).startsWith("color_") ? (
            <span className="app-settings-label-hint">
              Online shop theme color
            </span>
          ) : null}
        </div>
        <div className="app-settings-control" id={controlId}>
          {renderControl(setting)}
        </div>
      </div>
    );
  };

  const renderSection = (
    groupLabel: string,
    items: AppSettings[],
    compact = false,
  ) => (
    <div
      className={`app-settings-section ${compact ? "app-settings-compact-card" : ""}`}
    >
      <div className="app-settings-section-header">
        <div className="app-settings-section-icon">
          <i className={groupIconClass(groupLabel)} aria-hidden />
        </div>
        <div>
          <h5 className="app-settings-section-title">{groupLabel}</h5>
          <p className="app-settings-section-meta">
            {items.length} setting{items.length === 1 ? "" : "s"}
          </p>
        </div>
      </div>
      <div>{items.map(renderSettingRow)}</div>
    </div>
  );

  return (
    <UserLayout>
      <div className="app-settings-page">
        <div className="app-settings-hero">
          <div className="d-flex flex-wrap justify-content-between align-items-start gap-3">
            <div>
              <h2 className="h3 mb-0">Application Settings</h2>
              <p>
                Configure receipts, online shop branding, and system behaviour.
                Changes apply after you save.
              </p>
            </div>
            <div className="d-flex flex-wrap gap-2">
              <div className="app-settings-stat">
                <strong>{grouped.length}</strong>
                <span>Groups</span>
              </div>
              <div className="app-settings-stat">
                <strong>{totalSettings}</strong>
                <span>Settings</span>
              </div>
            </div>
          </div>
        </div>

        <div className="app-settings-toolbar">
          <div className="app-settings-toolbar-inner">
            <div className="app-settings-toolbar-status">
              <div className="app-settings-toolbar-icon" aria-hidden>
                <i className="fe-sliders" />
              </div>
              <div className="app-settings-toolbar-copy">
                <p className="app-settings-toolbar-title mb-0">
                  Branch preferences
                </p>
                <p className="app-settings-toolbar-sub mb-0">
                  Edit values below, then save to apply across the app
                </p>
              </div>
              {hasUnsavedChanges ? (
                <span className="app-settings-status-pill app-settings-status-pill--warn">
                  <span className="app-settings-status-dot" aria-hidden />
                  Unsaved changes
                </span>
              ) : (
                <span className="app-settings-status-pill app-settings-status-pill--ok">
                  <span className="app-settings-status-dot" aria-hidden />
                  All changes saved
                </span>
              )}
            </div>
            <div className="app-settings-toolbar-actions">
              <button
                type="button"
                className="app-settings-btn app-settings-btn--ghost"
                onClick={handleReset}
                disabled={loading || saving || !hasUnsavedChanges}
              >
                <i className="fe-rotate-ccw" aria-hidden />
                <span>Reset</span>
              </button>
              <button
                type="button"
                className="app-settings-btn app-settings-btn--primary"
                onClick={handleSaveAll}
                disabled={loading || saving}
              >
                {saving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden
                    />
                    <span>Saving…</span>
                  </>
                ) : (
                  <>
                    <i className="fe-save" aria-hidden />
                    <span>Save all</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="app-settings-loading">
            <div className="spinner-border mb-3" role="status">
              <span className="visually-hidden">Loading…</span>
            </div>
            <p className="mb-0">Loading settings…</p>
          </div>
        ) : grouped.length === 0 ? (
          <div className="app-settings-section p-4 text-center text-muted">
            No settings found. Add settings in the database to configure the app.
          </div>
        ) : (
          <div className="row g-3">
            {grouped.map(({ groupLabel, items }) =>
              items.length > 1 ? (
                <div
                  key={normalizeGroup(groupLabel)}
                  className="col-12"
                >
                  {renderSection(groupLabel, items)}
                </div>
              ) : (
                <div
                  key={items[0].id ?? groupLabel}
                  className="col-12 col-md-6 col-xl-4"
                >
                  {renderSection(groupLabel, items, true)}
                </div>
              ),
            )}
          </div>
        )}
      </div>
    </UserLayout>
  );
};

export default AppSetting;
